import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, DollarSign, BarChart2, Building2, Globe, Users, Info, ChevronDown, ChevronUp, LineChart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import TradingChartModal from './TradingChartModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StockInfo = ({ stockData }) => {
  const isPositive = stockData.change >= 0;
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isChartOpen, setIsChartOpen] = useState(false);

  const loadCompanyDetails = async () => {
    if (details) {
      setShowDetails(!showDetails);
      return;
    }

    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/stocks/${stockData.symbol}/details`);
      setDetails(response.data);
      setShowDetails(true);
    } catch (error) {
      console.error('Error loading company details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return 'N/A';
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toFixed(2)}`;
  };

  const formatPercent = (num) => {
    if (!num && num !== 0) return 'N/A';
    return `${(num * 100).toFixed(2)}%`;
  };

  return (
    <>
    <Card className="glass-effect border-0 shadow-lg card-hover" data-testid="stock-info-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-3xl font-bold">{stockData.symbol}</CardTitle>
            <p className="text-gray-600 text-lg mt-1">{stockData.name}</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold" data-testid="stock-price">
              ${stockData.current_price.toFixed(2)}
            </div>
            <div className={`flex items-center justify-end mt-1 text-lg font-semibold ${
              isPositive ? 'stock-positive' : 'stock-negative'
            }`} data-testid="stock-change">
              {isPositive ? <TrendingUp className="h-5 w-5 mr-1" /> : <TrendingDown className="h-5 w-5 mr-1" />}
              {isPositive ? '+' : ''}{stockData.change.toFixed(2)} ({isPositive ? '+' : ''}{stockData.change_percent.toFixed(2)}%)
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsChartOpen(true)}
              className="mt-2 bg-blue-50 border-blue-200 hover:bg-blue-100"
            >
              <LineChart className="h-4 w-4 mr-1" />
              View Chart
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white bg-opacity-50 rounded-lg p-4">
            <div className="flex items-center text-gray-600 mb-2">
              <BarChart2 className="h-4 w-4 mr-2" />
              <span className="text-sm">Volume</span>
            </div>
            <p className="text-xl font-semibold text-gray-900">{stockData.volume.toLocaleString()}</p>
          </div>
          
          {stockData.market_cap && (
            <div className="bg-white bg-opacity-50 rounded-lg p-4">
              <div className="flex items-center text-gray-600 mb-2">
                <DollarSign className="h-4 w-4 mr-2" />
                <span className="text-sm">Market Cap</span>
              </div>
              <p className="text-xl font-semibold text-gray-900">
                ${(stockData.market_cap / 1e9).toFixed(2)}B
              </p>
            </div>
          )}
          
          {stockData.pe_ratio && (
            <div className="bg-white bg-opacity-50 rounded-lg p-4">
              <div className="flex items-center text-gray-600 mb-2">
                <span className="text-sm">P/E Ratio</span>
              </div>
              <p className="text-xl font-semibold text-gray-900">{stockData.pe_ratio.toFixed(2)}</p>
            </div>
          )}
          
          {stockData.fifty_two_week_high && (
            <div className="bg-white bg-opacity-50 rounded-lg p-4">
              <div className="flex items-center text-gray-600 mb-2">
                <span className="text-sm">52W High/Low</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                ${stockData.fifty_two_week_high.toFixed(2)} / ${stockData.fifty_two_week_low.toFixed(2)}
              </p>
            </div>
          )}
        </div>

        {/* Company Details Button */}
        <div className="mt-4">
          <Button
            data-testid="view-company-details"
            onClick={loadCompanyDetails}
            disabled={loadingDetails}
            variant="outline"
            className="w-full"
          >
            {loadingDetails ? (
              'Loading...'
            ) : (
              <>
                <Info className="h-4 w-4 mr-2" />
                {showDetails ? 'Hide' : 'View'} Company Details & Vital Info
                {showDetails ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
              </>
            )}
          </Button>
        </div>

        {/* Detailed Company Information */}
        {showDetails && details && (
          <div className="mt-6 space-y-6 fade-in" data-testid="company-details">
            <Separator />

            {/* Company Overview */}
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center">
                <Building2 className="h-5 w-5 mr-2 text-indigo-600" />
                Company Overview
              </h3>
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4">
                <p className="text-gray-700 text-sm leading-relaxed">{details.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                  <div className="bg-white bg-opacity-60 rounded p-3">
                    <div className="flex items-center text-gray-600 text-xs mb-1">
                      <Building2 className="h-3 w-3 mr-1" />
                      Sector
                    </div>
                    <p className="font-semibold text-gray-900">{details.sector}</p>
                  </div>
                  <div className="bg-white bg-opacity-60 rounded p-3">
                    <div className="flex items-center text-gray-600 text-xs mb-1">
                      <BarChart2 className="h-3 w-3 mr-1" />
                      Industry
                    </div>
                    <p className="font-semibold text-gray-900">{details.industry}</p>
                  </div>
                  <div className="bg-white bg-opacity-60 rounded p-3">
                    <div className="flex items-center text-gray-600 text-xs mb-1">
                      <Globe className="h-3 w-3 mr-1" />
                      Country
                    </div>
                    <p className="font-semibold text-gray-900">{details.country}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  {details.employees && (
                    <div className="bg-white bg-opacity-60 rounded p-3">
                      <div className="flex items-center text-gray-600 text-xs mb-1">
                        <Users className="h-3 w-3 mr-1" />
                        Employees
                      </div>
                      <p className="font-semibold text-gray-900">{details.employees.toLocaleString()}</p>
                    </div>
                  )}
                  {details.website !== 'N/A' && (
                    <div className="bg-white bg-opacity-60 rounded p-3 col-span-2">
                      <div className="text-gray-600 text-xs mb-1">Website</div>
                      <a 
                        href={details.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-semibold"
                      >
                        {details.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Valuation Metrics */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Valuation Metrics</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Market Cap</p>
                  <p className="text-lg font-bold text-gray-900">{formatNumber(details.market_cap)}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">P/E Ratio (TTM)</p>
                  <p className="text-lg font-bold text-gray-900">{details.trailing_pe?.toFixed(2) || 'N/A'}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Forward P/E</p>
                  <p className="text-lg font-bold text-gray-900">{details.forward_pe?.toFixed(2) || 'N/A'}</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">PEG Ratio</p>
                  <p className="text-lg font-bold text-gray-900">{details.peg_ratio?.toFixed(2) || 'N/A'}</p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Price to Book</p>
                  <p className="text-lg font-bold text-gray-900">{details.price_to_book?.toFixed(2) || 'N/A'}</p>
                </div>
                <div className="bg-pink-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Price to Sales</p>
                  <p className="text-lg font-bold text-gray-900">{details.price_to_sales?.toFixed(2) || 'N/A'}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Beta</p>
                  <p className="text-lg font-bold text-gray-900">{details.beta?.toFixed(2) || 'N/A'}</p>
                </div>
                <div className="bg-teal-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Shares Out.</p>
                  <p className="text-lg font-bold text-gray-900">{details.shares_outstanding ? (details.shares_outstanding / 1e9).toFixed(2) + 'B' : 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Profitability */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Profitability & Growth</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Profit Margin</p>
                  <p className="text-lg font-bold text-green-700">{formatPercent(details.profit_margin)}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Operating Margin</p>
                  <p className="text-lg font-bold text-green-700">{formatPercent(details.operating_margin)}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">ROE</p>
                  <p className="text-lg font-bold text-green-700">{formatPercent(details.roe)}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">ROA</p>
                  <p className="text-lg font-bold text-green-700">{formatPercent(details.roa)}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Revenue</p>
                  <p className="text-lg font-bold text-blue-700">{formatNumber(details.revenue)}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Revenue Growth</p>
                  <p className="text-lg font-bold text-blue-700">{formatPercent(details.revenue_growth)}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Earnings Growth</p>
                  <p className="text-lg font-bold text-blue-700">{formatPercent(details.earnings_growth)}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Quarterly Rev Growth</p>
                  <p className="text-lg font-bold text-blue-700">{formatPercent(details.quarterly_revenue_growth)}</p>
                </div>
              </div>
            </div>

            {/* Dividends (if applicable) */}
            {details.dividend_yield && (
              <div>
                <h3 className="text-lg font-semibold mb-3">Dividend Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">Dividend Yield</p>
                    <p className="text-lg font-bold text-purple-700">{formatPercent(details.dividend_yield)}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">Dividend Rate</p>
                    <p className="text-lg font-bold text-purple-700">${details.dividend_rate?.toFixed(2) || 'N/A'}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">Payout Ratio</p>
                    <p className="text-lg font-bold text-purple-700">{formatPercent(details.payout_ratio)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Analyst Recommendations */}
            {details.recommendation !== 'N/A' && (
              <div>
                <h3 className="text-lg font-semibold mb-3">Analyst Recommendations</h3>
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Badge 
                      className={`text-lg px-4 py-2 ${
                        details.recommendation === 'BUY' || details.recommendation === 'STRONG_BUY' ? 'bg-green-500' :
                        details.recommendation === 'HOLD' ? 'bg-yellow-500' :
                        'bg-red-500'
                      } text-white`}
                    >
                      {details.recommendation.replace('_', ' ')}
                    </Badge>
                    <span className="text-sm text-gray-600">
                      Based on {details.number_of_analysts} analyst{details.number_of_analysts !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white bg-opacity-60 rounded p-3">
                      <p className="text-xs text-gray-600 mb-1">Target Low</p>
                      <p className="text-lg font-bold text-red-600">${details.target_low_price?.toFixed(2) || 'N/A'}</p>
                    </div>
                    <div className="bg-white bg-opacity-60 rounded p-3">
                      <p className="text-xs text-gray-600 mb-1">Target Mean</p>
                      <p className="text-lg font-bold text-yellow-600">${details.target_mean_price?.toFixed(2) || 'N/A'}</p>
                    </div>
                    <div className="bg-white bg-opacity-60 rounded p-3">
                      <p className="text-xs text-gray-600 mb-1">Target High</p>
                      <p className="text-lg font-bold text-green-600">${details.target_high_price?.toFixed(2) || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
    
    {/* Trading Chart Modal */}
    <TradingChartModal
      isOpen={isChartOpen}
      onClose={() => setIsChartOpen(false)}
      symbol={stockData.symbol}
    />
    </>
  );
};

export default StockInfo;
